/**
 * 実績紹介(/case)の事例データ
 * 一覧ページ(CaseList)と詳細ページ(CaseDetail)の両方で使用する
 */

export interface CaseSection {
  en: string;
  ja: string;
}

export interface CaseStat {
  value: string;
  label: string;
}

export interface CaseProblem {
  title: string;
  body: string;
}

export interface CaseFeature {
  image: string;
  alt: string;
  title: string;
  body: string;
  /** スマホ縦長キャプチャの場合 true(表示幅を絞る) */
  mobile?: boolean;
}

export interface CaseStudy {
  slug: string;
  category: "受託実績" | "自主運用サービス";
  tags: string[];
  /** 一覧カード用のサービス名 */
  name: string;
  /** 一覧カード用の説明文 */
  summary: string;
  /** 詳細ページのタイトル(改行位置は \n で指定) */
  title: string;
  /** 詳細ページのリード文 */
  lead: string;
  /** 一覧カードのカバー画像 */
  cover: string;
  coverAlt: string;
  /** 詳細ページのキービジュアル(main: 横長 / sub: スマホ縦長) */
  kv: { main: string; mainAlt: string; sub?: string; subAlt?: string };
  stats?: CaseStat[];
  sections: {
    problem: CaseSection;
    solution: CaseSection;
    outcome: CaseSection;
  };
  problems: CaseProblem[];
  features: CaseFeature[];
  outcomes: CaseProblem[];
  tech?: string;
  /** サービスの公開URL(自主運用サービスのみ) */
  url?: string;
}

export const CASE_STUDIES: CaseStudy[] = [
  {
    slug: "tsugunagi-shiken",
    category: "受託実績",
    tags: ["イベント運営システム", "リアルタイム同期"],
    name: "ツグナギシケン",
    summary:
      "リアル体験型デスゲームイベントのための運営システム。ポイント集計・ランキング・ゲーム進行をリアルタイムに一元管理。",
    title:
      "リアル体験型デスゲームイベントを支える\n運営システム『ツグナギシケン』",
    lead: "数十名が一斉に参加する初開催の体験型イベントのために、ポイント集計・ランキング表示・ゲーム進行をひとつにまとめた運営システムを開発。企画チームと一緒に、当日の運営フローまで含めてゼロから設計しました。",
    cover: "/images/pagesimage/case/tsugunagi-admin-dashboard.webp",
    coverAlt: "ツグナギシケン管理ダッシュボード",
    kv: {
      main: "/images/pagesimage/case/tsugunagi-admin-dashboard.webp",
      mainAlt: "ツグナギシケン管理ダッシュボード",
      sub: "/images/pagesimage/case/tsugunagi-player-game.webp",
      subAlt: "参加者用ゲーム画面",
    },
    stats: [
      { value: "〜50名", label: "同時接続数" },
      { value: "3秒以内", label: "ポイント反映・同期" },
      { value: "スタッフ0人", label: "運営メンバーのみで対応可能" },
    ],
    sections: {
      problem: { en: "PROBLEM", ja: "イベント運営の課題" },
      solution: { en: "SOLUTION", ja: "つくった仕組み" },
      outcome: { en: "OUTCOME", ja: "導入して楽になった点" },
    },
    problems: [
      {
        title: "テストプレイで見えた手集計の限界",
        body: "初開催のイベントに向けたテストプレイでは、スタッフが紙でポイントを計算・メモしながら運営。本番の人数規模では集計が追いつかず、順位発表までに会場の熱が冷めてしまうことが見えてきた。",
      },
      {
        title: "複数ゲームの進行管理が複雑",
        body: "ルール公開→ゲーム開始→結果発表というタイミング制御をゲームごとに行う必要があり、口頭と手作業の運営では進行ミスが起きやすい。",
      },
      {
        title: "参加者に順位が伝わらない",
        body: "参加者は自分の持ち点や現在順位をその場で知る手段がなく、競い合う緊張感やデスゲームらしい没入感を保ちにくい。",
      },
    ],
    features: [
      {
        image: "/images/pagesimage/case/tsugunagi-admin-dashboard.webp",
        alt: "ツグナギシケン管理ダッシュボード",
        title: "管理ダッシュボードで進行を一元管理",
        body: "ゲームごとの「ルール公開」「ゲーム開始」を管理画面からワンクリックで切り替え。会場の進行に合わせて、参加者全員のスマホ画面が一斉に切り替わります。",
      },
      {
        image: "/images/pagesimage/case/tsugunagi-player-home.webp",
        alt: "参加者用スマホ画面(持ち点と順位・ライブランキング)",
        title: "参加者のスマホがそのままゲーム端末に",
        body: "参加者は自分専用ページを開くだけ。持ち点・現在順位・ライブランキングが手元でリアルタイムに更新され、アプリのインストールは不要です。",
        mobile: true,
      },
      {
        image: "/images/pagesimage/case/tsugunagi-admin-ranking.webp",
        alt: "ランキング表示・ポイント付与画面",
        title: "ポイント付与・集計・順位計算は自動",
        body: "運営は会場でプラス・マイナスのボタンを押すだけ。Firestore のリアルタイム同期で数秒以内に全端末へ反映され、順位も自動で計算されます。",
      },
    ],
    outcomes: [
      {
        title: "集計作業がゼロに",
        body: "テストプレイで紙とメモに頼っていた集計・順位計算が完全に自動化され、スタッフは演出と参加者対応に集中できるようになりました。",
      },
      {
        title: "スタッフ5〜6人 → ほぼ0人に",
        body: "初回開催は5〜6人のスタッフで運営。改良を重ねた現行版では進行も集計も自動化され、4種目のゲームをスタッフほぼなしで運営できる体制になりました。",
      },
      {
        title: "会場の熱量が上がった",
        body: "順位がその場で参加者の手元に届くため、ポイントが動くたびに会場が沸く一体感が生まれました。",
      },
    ],
    tech: "Next.js / Firebase (Firestore リアルタイム同期) / QRコード連携",
  },
  {
    slug: "lonpa",
    category: "自主運用サービス",
    tags: ["意見交流サイト", "企画・開発・運営"],
    name: "Lonpa",
    summary:
      '"対立"を理解し"共感"を形にする意見交流サイト。賛成と反対、どちらの言い分も並べて眺められる場をつくっています。',
    title: '"対立"を理解し"共感"を形にする\n意見交流サイト『Lonpa』',
    lead: "SNSの議論は、いつのまにか勝ち負けになりがち。賛成と反対の言い分を同じ画面に並べて、「なぜそう思うのか」に目を向けられる場をつくりたい。そんな想いから、企画・開発・運営まで自分たちで続けているサービスです。",
    cover: "/images/pagesimage/case/lonpa-topic.webp",
    coverAlt: "Lonpaの議題ページ(賛成・反対の意見が並ぶ画面)",
    kv: {
      main: "/images/pagesimage/case/lonpa-topic.webp",
      mainAlt: "Lonpaの議題ページ(賛成・反対の意見が並ぶ画面)",
      sub: "/images/pagesimage/case/lonpa-mobile.webp",
      subAlt: "Lonpaのスマホ画面",
    },
    sections: {
      problem: { en: "PROBLEM", ja: "こんな課題意識から" },
      solution: { en: "SOLUTION", ja: "つくった仕組み" },
      outcome: { en: "NOW", ja: "いま目指していること" },
    },
    problems: [
      {
        title: "議論が勝ち負けになりがち",
        body: "SNSでは「論破」が目的化しやすく、意見の背景にある事情や気持ちが置き去りになってしまう。",
      },
      {
        title: "反対意見に触れる機会がない",
        body: "おすすめアルゴリズムの中では、自分と似た意見ばかりが流れてきて、対立の全体像が見えにくい。",
      },
      {
        title: "静かな共感が形にならない",
        body: "声の大きい意見だけが場に残り、「どちらの気持ちもわかる」という感覚は数として見えてこない。",
      },
    ],
    features: [
      {
        image: "/images/pagesimage/case/lonpa-desktop.webp",
        alt: "Lonpaのトップページ(議題の一覧とキャラクターの会話)",
        title: "身近な議題から、ゆるく始める",
        body: "「ハロウィンは仮装するべき?」のような身近なお題から社会的なテーマまで、キャラクターとの会話に導かれて気軽に議論へ入れます。",
      },
      {
        image: "/images/pagesimage/case/lonpa-topic.webp",
        alt: "議題ページ(賛成・反対の意見と割合グラフ)",
        title: "賛成と反対を、同じ画面に並べる",
        body: "議題ごとに賛成・反対の意見が色分けの吹き出しで並び、その割合はグラフで一目瞭然。どちらの立場の言い分も、フラットに眺められます。",
      },
      {
        image: "/images/pagesimage/case/lonpa-mobile.webp",
        alt: "Lonpaのスマホ画面",
        title: "自分も一言、参加できる",
        body: "「賛成する」「反対する」を選んで、自分の考えをひとこと投稿。論破ではなく、理解のための一言が積み重なっていきます。",
        mobile: true,
      },
    ],
    outcomes: [
      {
        title: "対立の構造が見える場に",
        body: "賛成と反対が同じ画面に並ぶことで、勝ち負けではなく「なぜそう思うか」に自然と目が向くようになっています。",
      },
      {
        title: "議題を育て続ける",
        body: "身近な話題から考えさせられるテーマまで、思わず一言いいたくなる議題を運営がキュレーションし続けています。",
      },
      {
        title: "自主運用だから磨き続けられる",
        body: "企画・デザイン・開発・運営まで一気通貫。ユーザーの反応を見ながら、体験を少しずつ改善しています。",
      },
    ],
    url: "https://lonpa.net/",
  },
  {
    slug: "osapon",
    category: "自主運用サービス",
    tags: ["イベントコミュニティ", "企画・開発・運営"],
    name: "OSAPON",
    summary:
      "「晴れた日は歩こう」を合言葉に、散歩イベントの企画・開催とコミュニティサイトの開発・運営を行っています。",
    title: "晴れた日は歩こう。\n散歩イベントコミュニティ『OSAPON』",
    lead: "東京の街をみんなでゆるく歩く散歩イベントを企画・開催し、その様子を伝えるコミュニティサイトも自分たちで開発・運営。イベントのリアルとWebの両方をつくっている、Lamactの自主運用サービスです。",
    cover: "/images/pagesimage/case/osapon-reports.webp",
    coverAlt: "OSAPONの過去イベントレポート一覧",
    kv: {
      main: "/images/pagesimage/case/osapon-desktop.webp",
      mainAlt: "OSAPONのトップページ",
      sub: "/images/pagesimage/case/osapon-mobile.webp",
      subAlt: "OSAPONのスマホ画面",
    },
    sections: {
      problem: { en: "PROBLEM", ja: "こんな課題意識から" },
      solution: { en: "SOLUTION", ja: "やっていること" },
      outcome: { en: "NOW", ja: "いま起きていること" },
    },
    problems: [
      {
        title: "大人になると、歩くきっかけがない",
        body: "運動不足は気になるけれど、ひとりの散歩はなかなか続かない。「なんとなく歩きたい」を後押しする場がない。",
      },
      {
        title: "イベントは参加のハードルが高い",
        body: "初対面の集まりは緊張するもの。目的がゆるく、ふらっと参加できるイベントは意外と少ない。",
      },
      {
        title: "楽しさが次につながらない",
        body: "せっかく開催しても、その場の雰囲気が伝わらなければ「次も行きたい」「行ってみたい」にはつながらない。",
      },
    ],
    features: [
      {
        image: "/images/pagesimage/case/osapon-desktop.webp",
        alt: "OSAPONのトップページ(開催予定イベントと過去レポート)",
        title: "街ごとの散歩イベントを企画・開催",
        body: "目白、日暮里、大塚、高輪ゲートウェイ…。東京の街をテーマに公式イベントを定期開催。コースの下見から当日の運営まで自分たちで行っています。",
      },
      {
        image: "/images/pagesimage/case/osapon-reports.webp",
        alt: "過去イベントのレポート一覧",
        title: "レポートで楽しさをおすそ分け",
        body: "開催後は写真つきレポートをサイトで公開。当日の雰囲気が伝わり、次の参加のきっかけになります。顔はスタンプで隠して、プライバシーにも配慮。",
      },
      {
        image: "/images/pagesimage/case/osapon-create-event.webp",
        alt: "イベント作成ツール(山手線の駅選択・日付・開始時間)",
        title: "イベント作成は、山手線をくるっと回して",
        body: "運営用のイベント作成ツールも自作。山手線の路線図を回して開催駅を選び、日付と開始時間を決めるだけで新しいイベントを登録できます。遊び心のあるUIは、裏側の管理ツールにも。",
      },
      {
        image: "/images/pagesimage/case/osapon-mobile.webp",
        alt: "OSAPONのスマホ画面",
        title: "コミュニティサイトも自分たちで開発",
        body: "イベント告知から過去レポートまでをまとめたサイトをスマホファーストで開発・運営。イベントのリアルとWebをひとつのチームでつくっています。",
        mobile: true,
      },
    ],
    outcomes: [
      {
        title: "2023年から継続開催",
        body: "千駄木の交流会から始まり、東京の各エリアで公式イベントを継続的に開催しています。",
      },
      {
        title: "企画から運営までワンストップ",
        body: "イベント企画・当日運営・サイト開発・レポート作成まで、すべて自分たちのチームで完結しています。",
      },
      {
        title: "レポートが次の参加を生む",
        body: "公開したレポートを見た人が次のイベントに顔を出してくれる、小さな循環が生まれています。",
      },
    ],
    url: "https://osapon.com/",
  },
];

export const findCaseStudy = (slug: string | undefined) =>
  CASE_STUDIES.find((caseStudy) => caseStudy.slug === slug);
